/**
 * =============================================================================
 * Footer.tsx - Footer principale dell'applicazione
 * =============================================================================
 * 
 * Componente footer con link utili, copyright e versione dell'applicazione.
 * La versione viene letta dal CHANGELOG.md per mantenere coerenza.
 * 
 * NOTA WHITE LABEL: Questo componente usa brandConfig per tutti i riferimenti
 * al brand. Non aggiungere testi hard-coded dell'organizzazione.
 * 
 * @author Development Team
 * @version 1.7.0
 * =============================================================================
 */

import { Link } from "react-router-dom";
import { brandConfig } from "@/config/brandConfig";
import { useTenantBrand } from "@/hooks/useTenantBrand";

/** Versione corrente dell'applicazione - sincronizzata con CHANGELOG.md */
export const APP_VERSION = "1.14.0";

export function Footer() {
  const { displayName, copyrightText, isLni } = useTenantBrand();

  return (
    <footer className="bg-ocean-deep text-sail pt-16 pb-8" data-tour-id="footer">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4" data-tour-id="footer-logo">
              {/* Logo CCFN mostrato solo per tenant LNI */}
              {isLni && (
                <img 
                  src={brandConfig.logoUrl} 
                  alt={brandConfig.logoAlt} 
                  className="w-10 h-10 object-contain"
                />
              )}
              <span className="font-display text-xl font-semibold">
                {displayName}
              </span>
            </Link>
            <p className="text-sail/70 max-w-md leading-relaxed text-pretty">
              {isLni ? `${brandConfig.tagline} per tutti i soci.` : "Piattaforma di formazione e didattica."}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold mb-4 text-sail">Collegamenti utili</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/corsi" className="text-sail/70 hover:text-sail transition-colors">
                  Tutti i corsi
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-sail/70 hover:text-sail transition-colors">
                  La mia formazione
                </Link>
              </li>
              <li>
                <Link to="/guida" className="text-sail/70 hover:text-sail transition-colors">
                  Guida utente
                </Link>
              </li>
              <li>
                <Link to="/auth" className="text-sail/70 hover:text-sail transition-colors">
                  Accedi / Registrati
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact - Hidden for now */}
          {/*
          <div>
            <h4 className="font-semibold mb-4 text-sail">Contatti</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sail/70">
                <Mail className="w-4 h-4" />
                <span>formazione@nautica.it</span>
              </li>
              <li className="flex items-center gap-2 text-sail/70">
                <Phone className="w-4 h-4" />
                <span>+39 02 1234567</span>
              </li>
              <li className="flex items-start gap-2 text-sail/70">
                <MapPin className="w-4 h-4 mt-1" />
                <span>Via del Mare, 1<br />00100 Roma</span>
              </li>
            </ul>
          </div>
          */}
        </div>

        <div className="border-t border-sail/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3 text-sail/50 text-sm flex-wrap">
            <p>{copyrightText}</p>
            {isLni && brandConfig.webmaster && (
              <>
                <span className="text-sail/30">•</span>
                <a 
                  href={brandConfig.webmaster.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sail transition-colors"
                >
                  Webmaster: {brandConfig.webmaster.name}
                </a>
              </>
            )}
            <span className="text-sail/30">•</span>
            <span className="text-sail/40 font-mono text-xs" title="Versione applicazione">
              v{APP_VERSION}
            </span>
          </div>
          <div className="flex flex-wrap gap-4 text-sail/50 text-sm">
            <Link to="/credits" className="hover:text-sail transition-colors">
              Crediti
            </Link>
            <Link to="/privacy" className="hover:text-sail transition-colors">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="hover:text-sail transition-colors">
              Cookie Policy
            </Link>
            <Link to="/termini" className="hover:text-sail transition-colors">
              Termini di servizio
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
